import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { getMeta, getSamplePrompts, getStateBrief } from "../lib/api";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";
import { StateBriefCard } from "../components/Briefs";
import { TrustBadge } from "../components/TrustBadge";
import { Select } from "./PartnersPage";
import { inr, num } from "../lib/format";

/** One state on one page, ready to print or save as PDF for a government meeting. */
export default function BriefPage() {
  const initial = new URLSearchParams(window.location.search).get("state") || "";
  const [meta, setMeta] = useState(null);
  const [state, setState] = useState(initial);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getMeta().then(setMeta).catch(setErr);
    if (initial) return;
    // Default to the state we fund most, not whatever sorts first alphabetically.
    getSamplePrompts()
      .then((p) => setState(p.top_states?.[0] || "Maharashtra"))
      .catch(() => setState("Maharashtra"));
  }, [initial]);

  useEffect(() => {
    if (!state) return;
    setData(null);
    setErr(null);
    getStateBrief(state).then(setData).catch(setErr);
  }, [state]);

  return (
    <div data-testid="brief-page">
      <div className="print:hidden">
        <PageHeader
          overline="Briefing pack"
          title="One state, one page, ready for the meeting"
          testId="brief-header"
          actions={
            <button
              onClick={() => window.print()}
              data-testid="print-brief-button"
              className="inline-flex items-center gap-2 bg-[#A01236] text-white rounded-xl px-4 py-2.5 text-[13px] font-medium hover:bg-[#760D28] transition-colors"
            >
              <Printer className="w-4 h-4" /> Print or save as PDF
            </button>
          }
        >
          Everything here is computed from mGrant and published sources, with the caveats printed
          alongside the figures.
        </PageHeader>

        <div className="flex gap-2 flex-wrap mb-6" data-testid="brief-filters">
          <Select value={state} onChange={setState} testId="filter-brief-state"
                  options={(meta?.states || []).map((s) => ({ value: s, label: s }))} />
        </div>
      </div>

      {err && <ErrorBox error={err} />}
      {!data && !err && state && <Loading />}

      {data && (
        <article className="space-y-4 max-w-4xl" data-testid="brief-body">
          <header className="hidden print:block border-b border-stone-300 pb-3 mb-3">
            <div className="text-[12px] font-semibold text-[#A01236]">
              Bajaj Finserv CSR · briefing note
            </div>
            <h1 className="text-[22px] font-bold">{data.state}</h1>
            <p className="text-[12px] text-stone-600">
              mGrant portfolio {data.portfolio.fy_range?.[0]}–
              {data.portfolio.fy_range?.[data.portfolio.fy_range.length - 1]} · prepared{" "}
              {new Date().toISOString().slice(0, 10)}
            </p>
          </header>

          <div className="print:hidden">
            <TrustBadge answerability={data.answerability} testId="brief-trust-badge" />
          </div>

          <StateBriefCard brief={data} />

          <section className="rounded-xl border border-stone-200 bg-white p-4 space-y-2"
                   data-testid="brief-trend">
            <h3 className="text-[13px] font-semibold text-stone-800">
              Committed by financial year
            </h3>
            <ul className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {Object.entries(data.committed_by_fy_inr || {}).map(([fy, v]) => (
                <li key={fy} className="border border-stone-200 rounded-lg px-3 py-2">
                  <div className="text-[12px] text-stone-600">{fy}</div>
                  <div className="num text-[14px] font-semibold text-stone-900">{inr(v)}</div>
                </li>
              ))}
            </ul>
            <p className="text-[12px] text-stone-600 leading-relaxed">
              Committed value, apportioned evenly where a grant covers several states.{" "}
              {num(data.in_flight_grants)} grants here are still in flight.
            </p>
          </section>

          {data.context_citations?.filter((c) => c.status === "loaded").length > 0 && (
            <section className="rounded-xl border border-dashed border-stone-300 bg-stone-50/70 p-4 space-y-2"
                     data-testid="brief-context">
              <h3 className="text-[13px] font-semibold text-stone-800">Context, not evidence</h3>
              {data.context_citations.filter((c) => c.status === "loaded").map((c) => (
                <div key={c.id} className="space-y-1">
                  <div className="text-[13px] font-semibold text-stone-900">
                    {c.title}
                    <span className="font-normal text-stone-600"> · {c.publisher}, {c.year}</span>
                  </div>
                  {(c.claims || []).map((x, i) => (
                    <p key={i} className="text-[13px] text-stone-700 leading-relaxed">
                      {x.claim}
                      <span className="block text-[12px] text-stone-600">{x.reference}</span>
                    </p>
                  ))}
                </div>
              ))}
            </section>
          )}

          <section className="rounded-xl border border-stone-200 bg-stone-50/70 p-4"
                   data-testid="brief-caveats">
            <h3 className="text-[13px] font-semibold text-stone-800 mb-1.5">
              What these figures do and do not say
            </h3>
            <ul className="space-y-1">
              {data.notes.map((n, i) => (
                <li key={i} className="text-[12px] text-stone-700 leading-relaxed flex gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#A01236] mt-1.5 shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>
        </article>
      )}
    </div>
  );
}
